import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./OfferGallery.scss";

export default function OfferGallery({ images, title }) {
  const photos = images.map(({ attributes: photo }) => ({
    src: photo.url || photo.formats?.large?.url || photo.formats?.medium?.url || photo.formats?.small?.url,
    thumb: photo.formats?.thumbnail?.url || photo.formats?.small?.url || photo.url,
    alt: photo.alternativeText,
  })).filter((photo) => photo.src);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const dialog = useRef(null);
  const opener = useRef(null);
  const viewport = useRef(null);
  const touch = useRef(null);
  const active = Math.min(index, Math.max(0, photos.length - 1));
  const count = photos.length;
  const move = (step) => { setIndex((current) => (current + step + count) % count); setZoom(1); };

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const modal = dialog.current;
    modal.showModal();
    return () => {
      modal.close();
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (viewport.current) {
      viewport.current.scrollTop = 0;
      viewport.current.scrollLeft = 0;
    }
  }, [active, zoom]);

  if (!count) return <p className="offer-gallery-empty">Zdjęcia oferty wkrótce.</p>;
  const alt = photos[active].alt || `${title} — zdjęcie ${active + 1}`;
  const gestures = {
    onTouchStart: (event) => {
      touch.current = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    },
    onTouchEnd: (event) => {
      if (!touch.current || zoom !== 1 || !event.changedTouches.length) return;
      const dx = event.changedTouches[0].clientX - touch.current.x;
      const dy = event.changedTouches[0].clientY - touch.current.y;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) move(dx < 0 ? 1 : -1);
      touch.current = null;
    },
  };
  const arrows = count > 1 && <>
    <button type="button" className="offer-gallery-prev" aria-label="Poprzednie zdjęcie" onClick={() => move(-1)}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7" /></svg></button>
    <button type="button" className="offer-gallery-next" aria-label="Następne zdjęcie" onClick={() => move(1)}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg></button>
  </>;
  const thumbnails = <div className="offer-gallery-thumbnails" aria-label="Wybierz zdjęcie">
    {photos.map((photo, number) => <button type="button" key={`${photo.src}-${number}`} aria-label={`Pokaż zdjęcie ${number + 1}`} aria-current={number === active ? "true" : undefined} onClick={() => { setIndex(number); setZoom(1); }}>
      <img src={photo.thumb || photo.src} alt="" loading="lazy" />
    </button>)}
  </div>;

  return <section className="offer-gallery" aria-label="Zdjęcia nieruchomości">
    <div className="offer-gallery-stage" {...gestures}>
      <button type="button" ref={opener} className="offer-gallery-open" onClick={() => { setZoom(1); setOpen(true); }} aria-label="Otwórz galerię na całym ekranie">
        <img src={photos[active].src} alt={alt} />
        <span className="offer-gallery-hint">⛶ Powiększ zdjęcie</span>
      </button>
      {arrows}
      <span className="offer-gallery-count" aria-live="polite">{active + 1} / {count}</span>
    </div>
    {thumbnails}
    {open && createPortal(<dialog ref={dialog} className="offer-gallery-modal" aria-label={`Galeria: ${title}`} onCancel={() => setOpen(false)} onKeyDown={(event) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") { event.preventDefault(); move(event.key === "ArrowLeft" ? -1 : 1); }
    }}>
      <div className="offer-gallery-toolbar">
        <span aria-live="polite">{active + 1} / {count}</span>
        <div>
          <button type="button" aria-label="Pomniejsz zdjęcie" disabled={zoom === 1} onClick={() => setZoom(Math.max(1, zoom - 0.5))}>−</button>
          <button type="button" aria-label="Resetuj powiększenie" onClick={() => setZoom(1)}>{Math.round(zoom * 100)}%</button>
          <button type="button" aria-label="Powiększ zdjęcie" disabled={zoom === 3} onClick={() => setZoom(Math.min(3, zoom + 0.5))}>+</button>
          <button type="button" autoFocus aria-label="Zamknij galerię" onClick={() => setOpen(false)}>✕</button>
        </div>
      </div>
      <div className="offer-gallery-modal-stage">
        <div ref={viewport} className="offer-gallery-viewport" {...gestures} onDoubleClick={() => setZoom(zoom === 1 ? 2 : 1)}>
          <div className="offer-gallery-zoom" style={{ width: `${zoom * 100}%`, height: `${zoom * 100}%` }}>
            <img src={photos[active].src} alt={alt} draggable="false" />
          </div>
        </div>
        {arrows}
      </div>
      <p className="offer-gallery-help">Przesuń, aby zmienić zdjęcie. Po powiększeniu przewijaj zdjęcie. Na komputerze użyj też strzałek i Esc.</p>
      {thumbnails}
    </dialog>, document.body)}
  </section>;
}
